import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";

import ModalImage from "react-modal-image";
import api from "../../services/api";

// Imagem dentro da bolha, no padrão do WhatsApp Web: cantos de 6 px, encostada na borda da bolha.
const useStyles = makeStyles(theme => ({
	messageMedia: {
		display: "block",
		objectFit: "cover",
		width: 280,
		maxWidth: "100%",
		height: "auto", // Redimensionar automaticamente a altura para manter a proporção
		margin: "0 0 3px -3px",
		borderRadius: 6,
		cursor: "pointer",
		backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "rgba(11,20,26,0.05)",
	}
}));

// Falha momentânea ao baixar (arquivo ainda não servido, rede, deploy) deixava a imagem quebrada
// até recarregar a página: o fetch rejeitava em silêncio e nada tentava de novo. Agora tenta
// 4 vezes com espera crescente e, se ainda assim falhar, mostra a URL direta com um parâmetro
// novo a cada tentativa (evita o cache do erro no navegador).
const RETRY_DELAYS = [1000, 3000, 8000];

// placeholderUrl: prévia local de quem acabou de enviar a imagem; fica na tela até a do
// servidor carregar (a bolha não pisca na troca da "enviando" pela definitiva).
// onOpen: quem usa tem visualizador próprio (galeria da conversa); o clique chama onOpen em
// vez de abrir a ampliação desta imagem sozinha.
const ModalImageCors = ({ imageUrl, placeholderUrl, onOpen }) => {
	const classes = useStyles();
	const [blobUrl, setBlobUrl] = useState("");
	const [fallbackVersion, setFallbackVersion] = useState(0);

	useEffect(() => {
		if (!imageUrl) return undefined;
		// prévia local (blob:/data:) já é a própria imagem: não há o que baixar
		if (/^(blob|data):/i.test(imageUrl)) return undefined;
		let cancelled = false;
		let createdUrl = "";
		let timer = null;
		setBlobUrl("");

		const fetchImage = async (attempt) => {
			try {
				const { data, headers } = await api.get(imageUrl, { responseType: "blob" });
				if (cancelled) return;
				createdUrl = window.URL.createObjectURL(
					new Blob([data], { type: headers["content-type"] })
				);
				setBlobUrl(createdUrl);
			} catch (err) {
				if (cancelled) return;
				if (attempt < RETRY_DELAYS.length) {
					timer = setTimeout(() => fetchImage(attempt + 1), RETRY_DELAYS[attempt]);
				} else {
					console.warn("[ModalImageCors] imagem indisponível após tentativas:", imageUrl);
				}
			}
		};
		fetchImage(0);

		return () => {
			cancelled = true;
			clearTimeout(timer);
			if (createdUrl) window.URL.revokeObjectURL(createdUrl);
		};
	}, [imageUrl]);

	const fallbackUrl = fallbackVersion
		? `${imageUrl}${imageUrl.includes("?") ? "&" : "?"}r=${fallbackVersion}`
		: imageUrl;
	const src = blobUrl || placeholderUrl || fallbackUrl;

	return (
		<span
			onError={() => {
				// <img> direto falhou (blob ainda não veio): tenta a URL de novo, sem cache, até 3x
				if (!blobUrl && fallbackVersion < 3) {
					setTimeout(() => setFallbackVersion((v) => v + 1), 1500 * (fallbackVersion + 1));
				}
			}}
		>
			{onOpen ? (
				<img
					className={classes.messageMedia}
					src={src}
					alt="image"
					role="button"
					tabIndex={0}
					onClick={onOpen}
					onDoubleClick={(e) => e.stopPropagation()}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							onOpen();
						}
					}}
				/>
			) : (
			<ModalImage
				className={classes.messageMedia}
				smallSrcSet={src}
				medium={src}
				large={src}
				alt="image"
				showRotate={true}
			/>
			)}
		</span>
	);
};

export default ModalImageCors;
